// The persistent bottom bar: Home · Pods · Quality · Andon · More.
//
// Five plain places of equal width and NO centre control — frame v2.12,
// template L500-515 with `fiveTabs`: the bar's `sc-for` draws every entry as
// `t.isTab` and the raised disc is nowhere in it. Scan and the raise kinds
// live on the floating + at the bottom-right (`PlusFab`), which is why the
// bar can give the fifth place its full width.
//
// Scan used to be the raised disc in the middle of a four-place bar, because
// it is the control an operator uses most in a day and the middle of the
// bottom edge is where a gloved thumb lands without looking. The + keeps
// that promise from the corner instead: one thumb reach from every tab, and
// the dial it opens puts Scan nearest the thumb (see `lib/fab`).

import { Icon, type IconName } from '@/components/ui/Icon';
import { useT } from '@/i18n/useT';
import { TABS } from '@/lib/tabs';
import type { Tab } from '@/store/slices/ui';

export interface TabBarProps {
  /** Null while a job is open: no place is "current" mid-pod. */
  tab: Tab | null;
  onTab: (t: Tab) => void;
  /** The count on each place's badge. Hidden at zero. See `lib/tabs`. */
  badges?: Partial<Record<Tab, number>>;
}

export function TabBar({ tab, onTab, badges = {} }: TabBarProps) {
  const t = useT();
  return (
    <nav
      aria-label={t.sectionsT}
      style={{
        flex: 'none',
        display: 'flex',
        alignItems: 'flex-end',
        gap: 2,
        background: 'var(--surface)',
        borderTop: '1px solid var(--line)',
        // The extra bottom padding is the home-indicator gutter on a phone in
        // standalone mode; without it the labels sit under the system bar.
        padding: '8px 10px calc(14px + env(safe-area-inset-bottom))',
      }}
    >
      {TABS.map((item) => (
        <TabButton
          key={item.key}
          icon={item.icon}
          label={t[item.label]}
          active={tab === item.key}
          onClick={() => onTab(item.key)}
          badge={badges[item.key] ?? 0}
        />
      ))}
    </nav>
  );
}

function TabButton({
  icon,
  label,
  active,
  onClick,
  badge = 0,
}: {
  icon: IconName;
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: number;
}) {
  const t = useT();
  return (
    <button
      type="button"
      className="press"
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        // 44px of target even though the glyph and label are smaller: this is
        // the control a gloved thumb hits most often in the day.
        minHeight: 44,
        justifyContent: 'center',
        color: active ? 'var(--ink)' : 'var(--sub)',
        position: 'relative',
      }}
    >
      <Icon name={icon} size={21} strokeWidth={2} />
      <span style={{ font: `${active ? 700 : 600} 10px Manrope,sans-serif` }}>{label}</span>
      {badge > 0 && (
        <span
          aria-label={`${badge} ${t.needsYouT}`}
          style={{
            position: 'absolute',
            top: 0,
            right: '50%',
            marginRight: -22,
            background: 'var(--danger)',
            color: '#fff',
            borderRadius: 999,
            minWidth: 16,
            height: 16,
            padding: '0 4px',
            display: 'grid',
            placeItems: 'center',
            font: '700 9.5px Manrope,sans-serif',
          }}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}
